<template>
  <div>
    <div class="card">
      <div class="card-header">
        <h3>{{ $t('application.applications') }}</h3>
      </div>
      <div class="card-body">
        <template v-if="!compactMode">
          <p v-if="!isReadOnly">{{ $t('application.select_apps_and_cities') }}</p>
          <p v-else>{{ $t('application.selected_apps') }}</p>
          <div class="table-responsive">
            <table class="table table-striped">
              <thead>
              <tr>
                <th width="200">{{ $t('application.app') }}</th>
                <th width="70">{{ $t('application.logo') }}</th>
                <th>{{ $t('application.cities') }}</th>
                <th v-if="showStatus" width="100">{{ $t('application.status_label') }}</th>
                <th v-if="showForceLegacy" width="120">{{ $t('application.force_legacy_label') }}</th>
                <th width="30" class="text-right"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="app in applications" :key="'row-' + app.id" class="cursor-pointer"
                  :aria-expanded="isExpanded(app.id)" @click="toggleExpanded(app.id)">
                <td>
                  {{ app.name }}
                  <input type="hidden" name="applicationIds[]" :value="app.id"/>
                </td>
                <td>
                  <img :src="app.logo" v-if="app.logo" class="img-fluid" :alt="app.name"/>
                </td>
                <td @click.stop>
                  <multiselect v-model="userApplications[app.id].cities"
                               :close-on-select="false"
                               :multiple="true"
                               :disabled="isReadOnly"
                               :placeholder="$t('application.select_placeholder')"
                               :selectLabel="$t('application.select_label')"
                               :deselectLabel="$t('application.deselect_label')"
                               :options="getCities(app.id)"/>

                  <input type="hidden" name="applicationCities[]"
                         :value="getJoinedCities(userApplications[app.id]?.cities)"/>

                  <div v-show="isExpanded(app.id)" class="mt-3">
                    <div class="d-flex justify-content-start mb-2" v-if="!isReadOnly">
                      <button type="button" class="btn btn-sm btn-outline-secondary" @click.stop="copyFromUser(app.id)">
                        {{ $t('application.use_panel_data') }}
                      </button>
                    </div>
                    <div class="form-group row align-items-center">
                      <label class="col-lg-3" :for="'applicationFirstName_' + app.id">{{
                          $t('application.first_name')
                        }}</label>
                      <div class="col-lg">
                        <input type="text" :id="'applicationFirstName_' + app.id" class="form-control"
                               v-model="userApplications[app.id].first_name" name="applicationFirstName[]"
                               :disabled="isReadOnly"/>
                      </div>
                    </div>
                    <div class="form-group row align-items-center">
                      <label class="col-lg-3" :for="'applicationLastName_' + app.id">{{
                          $t('application.last_name')
                        }}</label>
                      <div class="col-lg">
                        <input type="text" :id="'applicationLastName_' + app.id" class="form-control"
                               v-model="userApplications[app.id].last_name" name="applicationLastName[]"
                               :disabled="isReadOnly"/>
                      </div>
                    </div>
                    <div class="form-group row align-items-center">
                      <label class="col-lg-3" :for="'applicationEmail_' + app.id">{{ $t('application.email') }}</label>
                      <div class="col-lg">
                        <input type="email" :id="'applicationEmail_' + app.id" class="form-control"
                               v-model="userApplications[app.id].email" name="applicationEmail[]" :disabled="isReadOnly"/>
                      </div>
                    </div>
                    <div class="form-group row align-items-center">
                      <label class="col-lg-3" :for="'applicationPhone_' + app.id">{{ $t('application.phone') }}</label>
                      <div class="col-lg">
                        <input type="text" :id="'applicationPhone_' + app.id" class="form-control"
                               v-model="userApplications[app.id].phone" name="applicationPhone[]" :disabled="isReadOnly"/>
                      </div>
                    </div>
                  </div>
                </td>
                <td v-if="showStatus" @click.stop>
                  <template v-if="isReadOnly">
                    <!-- Read-only status display -->
                    <span v-if="userApplications[app.id].status === 'active'" class="badge badge-success">
                      {{ $t('application.status.active') }}
                    </span>
                    <span v-else-if="userApplications[app.id].status === 'waiting'" class="badge badge-warning">
                      {{ $t('application.status.waiting') }}
                    </span>
                    <span v-else class="badge badge-secondary">
                      {{ userApplications[app.id].status }}
                    </span>
                  </template>
                  <template v-else>
                    <!-- Editable status badges -->
                    <div class="d-flex gap-2">
                      <span
                          :class="['badge', 'badge-clickable', userApplications[app.id].status === 'active' ? 'badge-success' : 'badge-light']"
                          @click="userApplications[app.id].status = 'active'">
                        {{ $t('application.status.active') }}
                      </span>
                      <span
                          :class="['badge', 'badge-clickable', userApplications[app.id].status === 'waiting' ? 'badge-warning' : 'badge-light']"
                          @click="userApplications[app.id].status = 'waiting'">
                        {{ $t('application.status.waiting') }}
                      </span>
                    </div>
                    <!-- Hidden input for form submission -->
                    <input type="hidden" name="applicationStatus[]" :value="userApplications[app.id].status"/>
                  </template>
                </td>
                <td v-if="showForceLegacy" @click.stop>
                  <template v-if="isReadOnly">
                    <span v-if="userApplications[app.id].force_legacy_contributions" class="badge badge-warning">
                      {{ $t('application.force_legacy_on') }}
                    </span>
                    <span v-else class="badge badge-light">{{ $t('application.force_legacy_off') }}</span>
                  </template>
                  <template v-else>
                    <input type="checkbox" class="form-check-input position-static ml-0"
                           :id="'applicationForceLegacy_' + app.id"
                           :title="$t('application.force_legacy_hint')"
                           v-model="userApplications[app.id].force_legacy_contributions"/>
                    <!-- Hidden input jest emitowany zawsze, żeby indeksy applicationForceLegacy[]
                         pokrywały się z applicationIds[] po stronie serwera. -->
                    <input type="hidden" name="applicationForceLegacy[]"
                           :value="userApplications[app.id].force_legacy_contributions ? 1 : 0"/>
                  </template>
                </td>
                <td class="text-right" @click.stop>
                  <button type="button" class="btn btn-link chevron-btn" :aria-expanded="isExpanded(app.id)"
                          :aria-label="$t('application.app')" @click.stop="toggleExpanded(app.id)">
                    <span class="chevron-icon" :class="{ 'rotated': !isExpanded(app.id) }">▾</span>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-else>
          <p v-if="!isReadOnly">{{ $t('application.select_apps_and_cities') }}</p>
          <p v-else>{{ $t('application.selected_apps') }}</p>
          <div class="table-responsive" v-if="selectedApps.length">
            <table class="table table-striped">
              <thead>
              <tr>
                <th width="200">{{ $t('application.app') }}</th>
                <th width="70">{{ $t('application.logo') }}</th>
                <th>{{ $t('application.cities') }}</th>
                <th v-if="showStatus" width="100">{{ $t('application.status_label') }}</th>
                <th v-if="showForceLegacy" width="120">{{ $t('application.force_legacy_label') }}</th>
                <th width="30" class="text-right"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="appId in selectedApps" :key="'sel-' + appId" class="cursor-pointer"
                  :aria-expanded="isExpanded(appId)" @click="toggleExpanded(appId)">
                <td>
                  {{ appIdToName(appId) }}
                  <input type="hidden" name="applicationIds[]" :value="appId"/>
                </td>
                <td>
                  <img :src="getApp(appId)?.logo" v-if="getApp(appId)?.logo" class="img-fluid"
                       :alt="appIdToName(appId)"/>
                </td>
                <td @click.stop>
                  <multiselect v-model="userApplications[appId].cities"
                               :close-on-select="false"
                               :multiple="true"
                               :disabled="isReadOnly"
                               :placeholder="$t('application.select_placeholder')"
                               :selectLabel="$t('application.select_label')"
                               :deselectLabel="$t('application.deselect_label')"
                               :options="getCities(appId)"/>

                  <input type="hidden" name="applicationCities[]"
                         :value="getJoinedCities(userApplications[appId]?.cities)"/>

                  <div v-show="isExpanded(appId)" class="mt-3">
                    <div class="form-group row align-items-center mt-2">
                      <label class="col-lg-3" :for="'applicationFirstName_' + appId">{{
                          $t('application.first_name')
                        }}</label>
                      <div class="col-lg">
                        <input type="text" :id="'applicationFirstName_' + appId" class="form-control"
                               v-model="userApplications[appId].first_name" name="applicationFirstName[]"
                               :disabled="isReadOnly"/>
                      </div>
                    </div>
                    <div class="form-group row align-items-center">
                      <label class="col-lg-3" :for="'applicationLastName_' + appId">{{
                          $t('application.last_name')
                        }}</label>
                      <div class="col-lg">
                        <input type="text" :id="'applicationLastName_' + appId" class="form-control"
                               v-model="userApplications[appId].last_name" name="applicationLastName[]"
                               :disabled="isReadOnly"/>
                      </div>
                    </div>
                    <div class="form-group row align-items-center">
                      <label class="col-lg-3" :for="'applicationEmail_' + appId">{{ $t('application.email') }}</label>
                      <div class="col-lg">
                        <input type="email" :id="'applicationEmail_' + appId" class="form-control"
                               v-model="userApplications[appId].email" name="applicationEmail[]" :disabled="isReadOnly"/>
                      </div>
                    </div>
                    <div class="form-group row align-items-center">
                      <label class="col-lg-3" :for="'applicationPhone_' + appId">{{ $t('application.phone') }}</label>
                      <div class="col-lg">
                        <input type="text" :id="'applicationPhone_' + appId" class="form-control"
                               v-model="userApplications[appId].phone" name="applicationPhone[]" :disabled="isReadOnly"/>
                      </div>
                    </div>
                    <div class="d-flex justify-content-start" v-if="!isReadOnly">
                      <button type="button" class="btn btn-sm btn-outline-secondary" @click.stop="copyFromUser(appId)">
                        {{ $t('application.use_panel_data') }}
                      </button>
                    </div>
                  </div>
                </td>
                <td v-if="showStatus" @click.stop>
                  <template v-if="isReadOnly">
                    <!-- Read-only status display -->
                    <span v-if="userApplications[appId].status === 'active'" class="badge badge-success">
                      {{ $t('application.status.active') }}
                    </span>
                    <span v-else-if="userApplications[appId].status === 'waiting'" class="badge badge-warning">
                      {{ $t('application.status.waiting') }}
                    </span>
                    <span v-else class="badge badge-secondary">
                      {{ userApplications[appId].status }}
                    </span>
                  </template>
                  <template v-else>
                    <!-- Editable status badges -->
                    <div class="d-flex gap-2">
                      <span
                          :class="['badge', 'badge-clickable', userApplications[appId].status === 'active' ? 'badge-success' : 'badge-light']"
                          @click="userApplications[appId].status = 'active'">
                        {{ $t('application.status.active') }}
                      </span>
                      <span
                          :class="['badge', 'badge-clickable', userApplications[appId].status === 'waiting' ? 'badge-warning' : 'badge-light']"
                          @click="userApplications[appId].status = 'waiting'">
                        {{ $t('application.status.waiting') }}
                      </span>
                    </div>
                    <!-- Hidden input for form submission -->
                    <input type="hidden" name="applicationStatus[]" :value="userApplications[appId].status"/>
                  </template>
                </td>
                <td v-if="showForceLegacy" @click.stop>
                  <template v-if="isReadOnly">
                    <span v-if="userApplications[appId].force_legacy_contributions" class="badge badge-warning">
                      {{ $t('application.force_legacy_on') }}
                    </span>
                    <span v-else class="badge badge-light">{{ $t('application.force_legacy_off') }}</span>
                  </template>
                  <template v-else>
                    <input type="checkbox" class="form-check-input position-static ml-0"
                           :id="'applicationForceLegacy_' + appId"
                           :title="$t('application.force_legacy_hint')"
                           v-model="userApplications[appId].force_legacy_contributions"/>
                    <!-- Hidden input jest emitowany zawsze, żeby indeksy applicationForceLegacy[]
                         pokrywały się z applicationIds[] po stronie serwera. -->
                    <input type="hidden" name="applicationForceLegacy[]"
                           :value="userApplications[appId].force_legacy_contributions ? 1 : 0"/>
                  </template>
                </td>
                <td class="text-right" @click.stop>
                  <button type="button" class="btn btn-link chevron-btn" :aria-expanded="isExpanded(appId)"
                          :aria-label="$t('application.app')" @click.stop="toggleExpanded(appId)">
                    <span class="chevron-icon" :class="{ 'rotated': !isExpanded(appId) }">▾</span>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <hr v-if="selectedApps.length">

          <div class="form-group row align-items-center">
            <label class="col-lg-2">{{ $t('application.app') }}</label>
            <div class="col-lg-4">
              <multiselect v-model="newApplication.id"
                           :options="availableAppIds"
                           :custom-label="appIdToName"
                           :allow-empty="true"
                           :show-labels="false"
                           :close-on-select="true"
                           :placeholder="$t('application.select_placeholder')"/>
            </div>
            <label class="col-lg-2">{{ $t('application.city') }}</label>
            <div class="col-lg-4">
              <multiselect v-model="newApplication.cities"
                           :options="getCities(newApplication.id)"
                           :multiple="true"
                           :close-on-select="false"
                           :placeholder="$t('application.select_placeholder')"
                           :selectLabel="$t('application.select_label')"
                           :deselectLabel="$t('application.deselect_label')"/>
            </div>
          </div>
          <div class="text-right">
            <button type="button" class="btn btn-outline-primary" @click="addApplication()">
              {{ $t('application.add_application') }}
            </button>
          </div>
        </template>
      </div>

      <div class="card-footer" v-if="!hideSave">
        <button type="submit" class="btn btn-success">{{ $t('application.save') }}</button>
      </div>
    </div>
  </div>
</template>
<script setup>
import {computed, onBeforeMount, ref, watch} from 'vue';
import * as _ from 'lodash';
import Multiselect from 'vue-multiselect';

const props = defineProps({
  hideSave: {type: Boolean},
  // Disables all input fields. When unset, falls back to hideSave so existing
  // call sites that used hideSave to also "lock" the form (admin/user/show
  // preview) keep working unchanged. company/user/form passes :read-only="false"
  // to hide the inner save button while still allowing edits.
  readOnly: {type: Boolean, default: null},
  applications: {type: Array, required: true},
  user: {type: Object, required: true},
  compactMode: {type: Boolean, default: false},
  showStatus: {type: Boolean, default: false},
  // Checkbox "stare rozliczenie" per aplikacja - wyłącznie panel admina.
  showForceLegacy: {type: Boolean, default: false}
});

const isReadOnly = computed(() => props.readOnly === null ? props.hideSave : props.readOnly);

const error = ref('');
const userApplications = ref([]);
const newApplication = ref({id: null, cities: []});
const expanded = ref({});
const isExpanded = (id) => !!expanded.value[id];
const toggleExpanded = (id) => {
  if (id == null) return;
  expanded.value[id] = !expanded.value[id];
};

const selectedApps = computed(() => {
  return _.chain(userApplications.value)
      .map((val) => (val && val.cities && val.cities.length ? val.id : null))
      .filter()
      .value();
});

const appIdToName = (id) => {
  const a = getApp(id);
  return a ? a.name : '';
};

const availableAppIds = computed(() => {
  const alreadySelected = new Set(selectedApps.value);
  return props.applications
      .filter(a => !alreadySelected.has(a.id))
      .map(a => a.id);
});

const getJoinedCities = (cities) => {
  if (_.isNil(cities)) {
    return '';
  }
  return cities.join(',');
};

const getApp = (id) => _.find(props.applications, app => app.id === id);

const copyFromUser = (id) => {
  const app = getApp(id);
  if (!app) return;
  userApplications.value[id].first_name = props.user.first_name;
  userApplications.value[id].last_name = props.user.last_name;
  userApplications.value[id].email = props.user.email;
  userApplications.value[id].phone = props.user.phone;
};

const getCities = (id) => {
  const app = _.find(props.applications, (app) => app.id === id);
  if (!app || !app.cities) return [];
  return app.cities.split('\r\n');
};

watch(() => newApplication.value.id, () => {
  newApplication.value.cities = [];
});

const addApplication = () => {
  if (!newApplication.value.id || !newApplication.value.cities || newApplication.value.cities.length === 0) {
    return;
  }
  const id = newApplication.value.id;
  if (!userApplications.value[id]) {
    userApplications.value[id] = {
      id,
      cities: [],
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      status: 'active',
      force_legacy_contributions: false
    };
  }
  // merge unique cities
  const merged = _.uniq([...(userApplications.value[id].cities || []), ...newApplication.value.cities]);
  userApplications.value[id].cities = merged;
  // expand the newly added row for convenience
  expanded.value[id] = true;
  // reset picker
  newApplication.value = {id: null, cities: []};
};

onBeforeMount(() => {
  userApplications.value = [];
  _.forEach(props.applications, (application) => {
    userApplications.value[application.id] = {
      id: application.id,
      cities: [],
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      status: 'waiting',
      force_legacy_contributions: false
    };
  });

  _.forEach(props.user.applications, (application) => {
    userApplications.value[application.id].cities = application.pivot.cities === '' ? [] : application.pivot.cities.split(',');
    userApplications.value[application.id].first_name = application.pivot.first_name;
    userApplications.value[application.id].last_name = application.pivot.last_name;
    userApplications.value[application.id].email = application.pivot.email;
    userApplications.value[application.id].phone = application.pivot.phone;
    userApplications.value[application.id].status = application.pivot.status || 'waiting';
    // MySQL zwraca 0/1, Postgres true/false - normalizujemy do boolean.
    userApplications.value[application.id].force_legacy_contributions = !!application.pivot.force_legacy_contributions;
  });

  // Initialize expanded rows (collapsed by default in both views)
  expanded.value = {};
  // In edit view, newly added apps expand automatically via addApplication()
});
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style>
.chevron-btn {
  padding: 0.125rem 0.25rem;
  line-height: 1;
}

.chevron-icon {
  display: inline-block;
  font-size: 1.25rem; /* bigger arrow */
  transition: transform 0.15s ease-in-out;
}

/* When collapsed, rotate to point right; when expanded (default), arrow points down */
.chevron-icon.rotated {
  transform: rotate(-90deg);
}

.cursor-pointer {
  cursor: pointer;
}

.badge-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.badge-clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.badge-clickable.badge-light {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  border: 1px solid #dee2e6;
}

.badge-clickable.badge-light:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd;
}
</style>
